import { useState, useEffect, useCallback, useRef } from 'react';
import { getDocsTree, isHubPath, resolveDocModule, type DocTreeNode } from '@/lib/docs';
import { useDocsScroll } from '@/contexts/DocsScrollContext';

interface LoadedDoc {
  path: string;
  component: React.ComponentType | null;
}

const flattenDocsTree = (tree: DocTreeNode[]): string[] => {
  const result: string[] = [];

  const walk = (nodes: DocTreeNode[]) => {
    for (const node of nodes) {
      if (node.path) result.push(node.path);
      if (node.children) walk(node.children);
    }
  };

  walk(tree);
  return result;
};

export const useInfiniteDocScroll = (initialSlug?: string) => {
  const {
    setCurrentDocPath: setContextDocPath,
    setScrollProgress: setContextScrollProgress
  } = useDocsScroll();

  const [loadedDocs, setLoadedDocs] = useState<LoadedDoc[]>([]);
  const [allDocPaths, setAllDocPaths] = useState<string[]>([]);
  const [currentDocPath, setCurrentDocPath] = useState<string>('');

  // ---------- refs ----------
  const docRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const observers = useRef<Map<string, IntersectionObserver>>(new Map());
  const loadedDocsRef = useRef<LoadedDoc[]>([]);
  const allDocPathsRef = useRef<string[]>([]);
  const currentDocPathRef = useRef<string>('');
  const loadingDocsRef = useRef<Set<string>>(new Set());
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const rafScrollRef = useRef<number | null>(null);

  // ---------- keep refs in sync ----------
  useEffect(() => {
    loadedDocsRef.current = loadedDocs;
  }, [loadedDocs]);

  useEffect(() => {
    allDocPathsRef.current = allDocPaths;
  }, [allDocPaths]);

  useEffect(() => {
    currentDocPathRef.current = currentDocPath;

    if (!currentDocPath) return;
    const url = currentDocPath === 'index' ? '/docs' : `/docs/${currentDocPath}`;
    if (window.location.pathname !== url) {
      window.history.replaceState({}, '', url);
    }
  }, [currentDocPath]);

  // ---------- cleanup ----------
  useEffect(() => {
    return () => {
      observers.current.forEach(o => o.disconnect());
      observers.current.clear();
      if (rafScrollRef.current) cancelAnimationFrame(rafScrollRef.current);
    };
  }, []);

  // ---------- init doc paths ----------
  useEffect(() => {
    let cancelled = false;

    getDocsTree().then(tree => {
      if (cancelled) return;

      const flat = flattenDocsTree(tree);
      const valid: string[] = [];

      for (const path of flat) {
        if (resolveDocModule(path)) valid.push(path);
      }

      if (!valid.includes('index')) valid.unshift('index');
      setAllDocPaths(valid);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- loader ----------
  const loadDoc = useCallback(async (path: string) => {
    const importer = resolveDocModule(path);
    if (!importer) return null;
    const mod = await importer();
    return mod.default as React.ComponentType;
  }, []);

  // ---------- initial load ----------
  useEffect(() => {
    if (!allDocPaths.length) return;

    const startPath = initialSlug ?? 'index';
    let startIndex = allDocPaths.indexOf(startPath);

    // If not found, treat as a directory - find the first child path
    if (startIndex === -1) {
      startIndex = allDocPaths.findIndex(p =>
        p.startsWith(startPath + '/')
      );
    }

    if (startIndex === -1) return;

    // Section hubs are standalone landing pages: when one is the requested
    // doc it renders alone, and hub paths are otherwise skipped so the
    // reading chain never stacks a second hub (or any hub) beneath content.
    const startIsHub = isHubPath(allDocPaths[startIndex]);
    const toLoad: string[] = [allDocPaths[startIndex]];
    if (!startIsHub) {
      for (
        let i = startIndex + 1;
        i < allDocPaths.length && toLoad.length < 3;
        i++
      ) {
        if (!isHubPath(allDocPaths[i])) toLoad.push(allDocPaths[i]);
      }
    }

    let cancelled = false;

    Promise.all(
      toLoad.map(async path => ({
        path,
        component: await loadDoc(path)
      }))
    ).then(docs => {
      if (cancelled) return;
      setLoadedDocs(docs);
      setCurrentDocPath(allDocPaths[startIndex]);
      setContextDocPath(allDocPaths[startIndex]);
    });

    return () => {
      cancelled = true;
    };
  }, [allDocPaths, initialSlug, loadDoc, setContextDocPath]);

  // ---------- scroll progress (rAF throttled) ----------
  useEffect(() => {
    const container =
      document.getElementById('docs-scroll-container') ||
      document.querySelector('main');

    if (!container) return;
    scrollContainerRef.current = container as HTMLElement;

    const onScroll = () => {
      if (rafScrollRef.current != null) return;

      rafScrollRef.current = requestAnimationFrame(() => {
        rafScrollRef.current = null;

        const path = currentDocPathRef.current;
        const el = docRefs.current.get(path);
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const cRect = container.getBoundingClientRect();

        const visibleTop = Math.max(0, cRect.top - rect.top);
        const progress = Math.min(
          100,
          Math.max(0, (visibleTop / rect.height) * 100)
        );

        setContextScrollProgress(progress);
      });
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => container.removeEventListener('scroll', onScroll);
  }, [setContextScrollProgress]);

  // ---------- register ----------
  const registerDocRef = useCallback(
    (docPath: string, element: HTMLDivElement | null) => {
      if (!element) {
        docRefs.current.delete(docPath);
        observers.current.get(docPath)?.disconnect();
        observers.current.get(`${docPath}-bottom`)?.disconnect();
        observers.current.delete(docPath);
        observers.current.delete(`${docPath}-bottom`);
        return;
      }

      docRefs.current.set(docPath, element);

      const container = scrollContainerRef.current;
      if (!container) return;

      // ---------- active doc observer ----------
      const visibilityObserver = new IntersectionObserver(
        entries => {
          let bestPath = currentDocPathRef.current;
          let bestScore = -Infinity;

          for (const [path, el] of docRefs.current.entries()) {
            const r = el.getBoundingClientRect();
            const c = container.getBoundingClientRect();

            const visible =
              Math.max(0, Math.min(r.bottom, c.bottom) - Math.max(r.top, c.top));

            if (visible <= 0) continue;

            // Reading line: a doc only becomes active once the reader has
            // actually scrolled to it. Lazily-appended docs sit below this
            // line, so they load into the page without stealing the active
            // state (and with it the URL, title and canonical).
            if (r.top > c.top + 80) continue;

            // Weight distance from the container top at full strength so the
            // topmost visible doc always ranks first. A weaker factor let a
            // fully-visible child doc out-score a short section hub right
            // after paint.
            const distancePenalty = Math.abs(r.top - c.top);
            const score = visible - distancePenalty;

            if (score > bestScore) {
              bestScore = score;
              bestPath = path;
            }
          }

          if (bestPath && bestPath !== currentDocPathRef.current) {
            setCurrentDocPath(bestPath);
            setContextDocPath(bestPath);
          }
        },
        {
          root: container,
          threshold: [0, 0.25, 0.5]
        }
      );

      visibilityObserver.observe(element);
      observers.current.set(docPath, visibilityObserver);

      // ---------- bottom loader ----------
      const sentinel = element.querySelector(
        `[data-doc-bottom="${docPath}"]`
      );

      if (!sentinel) return;

      const bottomObserver = new IntersectionObserver(
        async entries => {
          if (!entries.some(e => e.isIntersecting)) return;

          const idx = allDocPathsRef.current.indexOf(docPath);
          if (idx === -1) return;

          // Skip section hubs: the reading chain flows from content doc to
          // content doc, never auto-loading a hub page mid-scroll.
          let nextPath: string | undefined;
          for (let j = idx + 1; j < allDocPathsRef.current.length; j++) {
            const candidate = allDocPathsRef.current[j];
            if (!isHubPath(candidate)) {
              nextPath = candidate;
              break;
            }
          }
          if (!nextPath) return;

          if (
            loadingDocsRef.current.has(nextPath) ||
            loadedDocsRef.current.some(d => d.path === nextPath)
          ) {
            return;
          }

          loadingDocsRef.current.add(nextPath);
          try {
            const comp = await loadDoc(nextPath);
            setLoadedDocs(prev => [...prev, { path: nextPath, component: comp }]);
          } finally {
            loadingDocsRef.current.delete(nextPath);
          }
        },
        {
          root: container,
          rootMargin: '600px',
          threshold: 0
        }
      );

      bottomObserver.observe(sentinel);
      observers.current.set(`${docPath}-bottom`, bottomObserver);
    },
    [loadDoc, setContextDocPath]
  );

  return {
    loadedDocs,
    registerDocRef,
    allDocPaths,
  };
};
